// ABOUTME: Waits for the team's FUB list-sync to complete, then triggers the
// ABOUTME: bucketer run via onReady(). Single responsibility — display only.
"use client";

import { useEffect, useRef, useState } from "react";
import { api } from "@/convex/_generated/api";
import { useQuery } from "convex/react";
import { Row } from "../../_components/row/Row";
import { Text } from "../../_components/text/Text";
import { Column } from "../../_components/column/Column";
import { Loader2, Clock } from "lucide-react";

// One minute is more generous than ContactSyncStatus's 30s — FUB list-syncs
// can run longer for larger databases.
const ABANDON_TIMEOUT_MS = 60_000;

interface NativeFubPreparingProps {
  onReady: () => void;
}

export function NativeFubPreparing({ onReady }: NativeFubPreparingProps) {
  const syncProgress = useQuery(api.fub.syncProgress.queries.getSyncProgress);
  const hasTriggeredRef = useRef(false);
  const [abandoned, setAbandoned] = useState(false);

  useEffect(() => {
    if (hasTriggeredRef.current) return;
    if (!syncProgress) return;
    if (syncProgress.status !== "completed") return;
    hasTriggeredRef.current = true;
    onReady();
  }, [syncProgress, onReady]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasTriggeredRef.current) {
        setAbandoned(true);
      }
    }, ABANDON_TIMEOUT_MS);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  if (syncProgress?.status === "failed") {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-5">
        <Row align className="gap-3">
          <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
            <Clock className="w-5 h-5 text-rose-600" />
          </div>
          <Column noGap>
            <Text fat className="text-rose-900">
              Contact sync failed
            </Text>
            <Text sm className="text-rose-700">
              We couldn't sync your Follow Up Boss contacts. Please reconnect and try again.
            </Text>
          </Column>
        </Row>
      </div>
    );
  }

  if (abandoned) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-5">
        <Column className="gap-3">
          <Row align className="gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <Column noGap>
              <Text fat className="text-amber-900">
                Initial sync is taking longer than expected.
              </Text>
              <Text sm>
                Your Follow Up Boss contacts are still syncing. This can take several minutes for larger databases.
              </Text>
            </Column>
          </Row>
          <Column tightGap className="pl-[52px]">
            <Text sm>
              Let's keep setting up. Come back to this page when your database is ready to enrich — we'll pick up right where you left off.
            </Text>
          </Column>
        </Column>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-5">
      <Row align className="gap-3">
        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
          <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
        </div>
        <Column noGap>
          <Text fat className="text-blue-900">
            Syncing your Follow Up Boss contacts…
          </Text>
          <Text sm className="text-blue-700">
            Pulling your contacts so we can start enrichment right away.
          </Text>
        </Column>
      </Row>
    </div>
  );
}
